import { Dialog as HeadlessDialog, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import { type PropsWithChildren } from 'react';
import { Icon } from '#common/components/Icon.tsx';
import { IconButton } from '#common/components/IconButton.tsx';
import { type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';

const iconColor = {
    default: 'text-black',
    alert: 'text-orange-600'
};

const iconBackgroundColor = {
    default: 'bg-yellow-300',
    alert: 'bg-orange-100'
};

export function Dialog({
    title,
    description,
    icon,
    showCloseIcon,
    open,
    onClose,
    children,
    type = 'default',
    appearance = 'legacy',
    theme = 'dark',
}: PropsWithChildren<{
    title: string | React.ReactNode;
    description: string | React.ReactNode;
    icon: string;
    showCloseIcon?: boolean;
    open: boolean;
    type?: 'default' | 'alert';
    appearance?: 'legacy' | 'unified';
    theme?: CoreTheme;
    onClose: () => void;
}>) {
    const unified = appearance === 'unified';
    return (
        <HeadlessDialog
            open={open}
            onClose={onClose}
            className={clsx(unified && 'core-booking-theme')}
            data-theme={unified ? theme : undefined}
        >
            <div
                className={clsx(
                    'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center transition-opacity duration-200 z-400',
                    unified ? 'core-booking-viewport core-booking-align p-0 sm:p-6' : 'p-6'
                )}
            >
                <div
                    className={clsx(
                        'max-w-md w-full mx-auto relative',
                        unified ? 'core-booking-panel' : 'rounded-lg shadow-2xl',
                        !unified && (type === 'alert' ? 'bg-red-600' : 'bg-white')
                    )}
                >
                    <DialogPanel className={clsx(unified && 'core-contact-dialog-content')}>
                        {showCloseIcon && (
                            <IconButton
                                icon='heroicons--x-mark'
                                className={clsx(
                                    'core-dialog-close absolute top-4 right-4 cursor-pointer text-2xl',
                                    unified
                                        ? 'opacity-60 hover:bg-[var(--core-soft-bg)] hover:opacity-100'
                                        : type === 'alert'
                                        ? 'text-white hover:text-red-300'
                                        : 'text-gray-500 hover:text-red-700'
                                )}
                                onClick={onClose}
                                aria-label='Close dialog'
                            />
                        )}
                        <div className='text-center p-4 pt-12'>
                            <div
                                className={clsx(
                                    'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4',
                                    unified
                                        ? type === 'alert'
                                            ? 'bg-[color-mix(in_srgb,var(--core-accent)_16%,transparent)]'
                                            : 'bg-[color-mix(in_srgb,var(--core-blue)_16%,transparent)]'
                                        : iconBackgroundColor[type]
                                )}
                            >
                                <Icon
                                    icon={icon}
                                    className={clsx(
                                        'text-3xl',
                                        unified
                                            ? type === 'alert' ? 'text-[var(--core-accent)]' : 'text-[var(--core-blue)]'
                                            : iconColor[type]
                                    )}
                                />
                            </div>
                            <h3
                                className={clsx(
                                    'text-2xl font-bold mb-3',
                                    unified
                                        ? 'text-[var(--core-text)]'
                                        : type === 'alert'
                                        ? 'text-white'
                                        : 'text-gray-900'
                                )}
                            >
                                {title}
                            </h3>
                            <div
                                className={clsx(
                                    'mb-6',
                                    unified ? 'core-booking-muted' : type === 'alert' ? 'text-red-200' : 'text-gray-600'
                                )}
                            >
                                {description}
                            </div>
                            {children}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </HeadlessDialog>
    );
}
